import {
  getGridBooleanOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterItem,
  GridLogicOperator,
  GridRowSelectionModel,
  GridToolbarProps,
} from "@mui/x-data-grid";
import {
  ContactStatus,
  currencyFormatter,
  displayPhoneNumber,
  IContactPerson,
  IContactResponseItem,
  INSIGHTS_UNASSIGNED,
  SavedFilterType,
  SendgridMessageEventType,
  toDecimal,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DataGridPro,
  DataGridProProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { Tooltip } from "@mui/material";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/20/solid";
import { When } from "react-if";
import { useLocation } from "react-router";
import { toast } from "react-toastify";

import {
  ContactTableFields,
  useContactsV2,
  useContactTableModels,
  useContactTableParams,
} from "../../../hooks/useContacts";
import { usePersistedTableModels } from "../../../hooks/usePersistedTableModels";
import { NotesCellButton } from "../../Molecules/NotesDrawer";
import { WrappedLink } from "../../WrappedLink";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import {
  commonConfig,
  filterColumns,
  getColumnForNewFilter,
  getTogglableColumns,
  onRowSelectionChangeWrapper,
  toSavedFilterExportParams,
} from "../../../lib/datagrid";
import { Badge } from "../../../../common/Atoms/Badge";
import { Dropdown } from "../../../../common/Atoms/Dropdown";
import { useGroups } from "../../../hooks/useGroups";
import { useAddContactsToGroupMutation } from "../../../services/api/groupApi/group";
import { useContactTags } from "../../../hooks/useContactTags";
import { useCurrentOrgUsers } from "../../../hooks/useCurrentOrgUsers";
import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { useAccountingSystemName } from "../../../hooks/useAccountingSystemName";
import { EmailStatusPill, emailStatusLabel } from "../../Common/EmailStatusPill";
import { useExportAllContactsMutation, useSyncContactsMutation } from "../../../services/api/contactsApi/contacts";
import { Button } from "../../../../common/Atoms/Button";
import { Notification } from "../../../../common/Atoms/Notification";
import { env } from "../../../../common/lib/env";
import { CustomGenericToolbar } from "../../Common/Datagrid/CustomGenericToolbar";
import { ApiSavedFilterPanel } from "../../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../../Common/Datagrid/DatagridCard";
import { useApplySavedFilterFromUrl } from "../../../hooks/useApplySavedFilterFromUrl";
import { useFetchAggNamesQuery } from "../../../services/api/aggregateNamesApi/aggregateNames";
import {
  CheckMobileNumbersButton,
  FixSelectedMobileNumbersButton,
  InvalidMobileFixButton,
} from "../CheckMobileNumbersButton";
import { MobileNumberFixModalContent } from "../../Modals/MobileNumberFixModal";
import { useAccountingSystemTerm } from '../../../hooks/useAccountingSystemTerm';
import { classNames } from "../../../../common/lib/classNames";
import { creditScoreTone } from "../ContactPage/V2/ContactCreditScoreBadge";

import { ApplyTags } from "./ApplyTags";
import { BulkAssignContact } from "./BulkAssignContact";
import { ChildContactsPopover, ParentContactLink } from "./ContactRelationship";
import { FreezeContacts } from "./FreezeContacts";
import { QuickActions } from "./QuickActions";
import { SetParentContact } from "./SetParentContact";

interface ContactTableRootProps {
  context?: `group`;
}

// augment the props for the toolbar slot
// eslint-disable-next-line quotes
declare module "@mui/x-data-grid"{
  interface ToolbarPropsOverrides {
    setRowSelectionModel: (value: GridRowSelectionModel) => void;
    rowSelectionModel: GridRowSelectionModel;
    onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
    selectedContacts: number[];
  }
}

type ToolbarProps = GridToolbarProps & {
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (value: GridRowSelectionModel) => void;
  selectedContacts: number[];
};

type TransformedRows = Omit<IContactResponseItem
  & Omit<IContactPerson, `id`>, `id`>
  & {
    contactPersonId: string;
    contactId: number;
    hierarchy: string[];
    id: string;
    selectable: boolean;
  };

const groupingColDef: DataGridProProps[`groupingColDef`] = {
  headerName: ``,
  width: 1,
  minWidth: 1,
  resizable: false,
  renderCell: () => <CustomGridTreeDataGroupingCell />,
};

const neverShowColumns = [
  ContactTableFields.status,
  ContactTableFields.paidniceGroup,
  ContactTableFields.tags,
  ContactTableFields.personTags,
  ContactTableFields.mobileNumber,
  ContactTableFields.externalAccountNumber,
  ContactTableFields.includeInNotifications,
  ContactTableFields.portalUrl,
  ContactTableFields.externalId,
  ContactTableFields.hasActivePaymentPlan,
];

const getTreeDataPath: DataGridProProps[`getTreeDataPath`] = row => row.hierarchy;

const defaultColumnVisibilityModel = {
  [GRID_TREE_DATA_GROUPING_FIELD]: false,
  [ContactTableFields.status]: false,
  [ContactTableFields.paidniceGroup]: false,
  [ContactTableFields.tags]: false,
  [ContactTableFields.personTags]: false,
  [ContactTableFields.mobileNumber]: false,
  [ContactTableFields.externalAccountNumber]: false,
  [ContactTableFields.includeInNotifications]: false,
  [ContactTableFields.portalUrl]: false,
  [ContactTableFields.externalId]: false,
  [ContactTableFields.parentContactName]: false,
  [ContactTableFields.daysSinceLastPayment]: false,
  [ContactTableFields.latestEmailEvent]: false,
  [ContactTableFields.hasActivePaymentPlan]: false,
};

export function ContactTableRoot({
  context,
}: ContactTableRootProps) {
  const { data, isLoading, isFetching } = useContactsV2();
  const tableModels = useContactTableModels();
  const { filterModel, setFilterModel, setPaginationModel, setSortModel, sortModel, paginationModel, resetModels } = tableModels;
  const savedFilterExportParams = toSavedFilterExportParams(useContactTableParams());
  const restoredModels = usePersistedTableModels(`contact`, tableModels);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: `include`,
    ids: new Set(),
  });
  const externalAggregateNameLabel = useAccountingSystemTerm(`Contact Groups`);
  const externalAccountNumberLabel = useAccountingSystemTerm(`Account Number`);
  // Single contact AI fix, opened from a row's invalid mobile button. The id is kept while the modal closes
  const [mobileFix, setMobileFix] = useState<{ contactId?: number; open: boolean }>({ open: false });
  const openMobileFix = (contactId: number) => setMobileFix({ contactId, open: true });
  const [ syncContacts, { isLoading: syncContactsLoading } ] = useSyncContactsMutation();
  const apiRef = useGridApiRef();
  const currentOrg = useGetSelectedOrganisation();
  const currentOrgUsers = useCurrentOrgUsers(true);
  const { tags } = useContactTags();
  const { data: groups } = useGroups();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const savedFilterId = useApplySavedFilterFromUrl(apiRef, SavedFilterType.Contact, { defaultColumnVisibilityModel });
  const { data: aggNamesResponse } = useFetchAggNamesQuery({
    organisationId: currentOrg?.id,
  }, {
    skip: !currentOrg?.id,
  });

  const {
    contacts,
    totalCount,
    totalVersionOneCount,
  } = data;

  const onRemoveFilter = (filterId: GridFilterItem[`id`]) => {
    setFilterModel({
      items: filterModel.items.filter(item => item.id !== filterId),
    });
  };

  // On first mount, reset the filters, unless we're restoring a saved filter
  useEffect(() => {
    if (urlParams.has(`persist`) || savedFilterId || restoredModels) {
      // Do nothing
    }
    else {
      resetModels();
    }
  }, []);

  const externalAggregateNameOptions = useMemo(() => {
    if (!aggNamesResponse || !aggNamesResponse.aggregateNames) return [];

    return aggNamesResponse.aggregateNames.filter(name => !!name).map(name => ({
      label: name,
      value: name,
    }));
  }, [aggNamesResponse]);

  const tagOptions = useMemo(() => {
    if (!tags) return [];

    return tags.map(tag => ({
      label: tag,
      value: tag,
    }));
  }, [tags]);

  const groupOptions = useMemo(() => {
    if (!groups) return [];

    return groups.map(g => ({
      label: g.title,
      value: g.id,
    }));
  }, [groups]);

  // Extract all contact persons to the top line to let MUI group them
  const flattenedContacts = useMemo(() => {
    if (!contacts) return [];

    const flattened: Array<TransformedRows> = [];

    contacts.forEach(contact => {
      const primary = contact.primaryContactPerson;

      flattened.push({
        ...contact,
        ...primary,
        id: `${contact.id}_${primary.id}`,
        contactId: contact.id,
        contactPersonId: primary.id,
        hierarchy: [contact.id.toString()],
        selectable: true,
        personTags: primary.personTags || [],
      });

      contact.contactPersons.forEach(additional => {
        flattened.push({
          ...contact,
          ...additional,
          id: `${contact.id}_${additional.id}`,
          contactId: contact.id,
          contactPersonId: additional.id,
          hierarchy: [contact.id.toString(), additional.id],
          selectable: false,
          // Set empty values for some fields to keep table clean
          name: ``,
          tags: [],
          personTags: additional.personTags || [],
          externalAggregateNames: [],
          externalAccountNumber: ``,
          unpaidInvoiceCount: null,
          oldestDaysOverdue: null,
          outstandingBalanceHome: null,
          overdueBalanceHome: null,
          totalRevenueHomeLast12Months: null,
          creditScore: null,
          latestEmailEvent: undefined,
        });
      });
    });

    return flattened;
  }, [contacts]);

  const columns: GridColDef<TransformedRows>[] = useMemo(() => {
    const result: GridColDef<TransformedRows>[] = [
      {
        field: ContactTableFields.name,
        headerName: `Name`,
        width: 220,
        type: `string`,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => {
          if (!row.selectable) return null;

          return (
            <div className={ `flex flex-col` }>
              <span className={ `flex` }>
                <WrappedLink
                  to={ `/contacts/${ row.contactId }` }
                >
                  <Paragraph
                    variant={ `link` }
                    as={ `span` }
                  >
                    { row.name }
                  </Paragraph>
                </WrappedLink>

                <div className={ `flex flex-wrap ml-2` }>
                  <ChildContactsPopover childContacts={ row.childContacts } />
                  {
                    row.tags.map(tag => (
                      <Badge
                        key={ tag + row.id }
                        className={ `max-w-fit max-h-fit mr-2` }
                        color={ `yellow` }
                      >
                        { tag }
                      </Badge>
                    ))
                  }
                </div>
              </span>

              <ParentContactLink
                parentContactId={ row.parentContactId }
                parentContactName={ row.parentContactName }
                parentContactArchived={ row.parentContactArchived }
              />

              <WrappedLink
                to={ `/groups/${ row.groupId }` }
              >
                <Paragraph
                  variant={ `link` }
                  as={ `span` }
                  className={ `` }
                  size={ `xs` }
                >
                  { row.groupTitle }
                </Paragraph>
              </WrappedLink>

              <Tooltip title={ `Account Number` }>
                <span>
                  <Paragraph
                    as={ `span` }
                    variant={ `help` }
                  >
                    { row.externalAccountNumber }
                  </Paragraph>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        field: ContactTableFields.parentContactName,
        headerName: `Parent Contact`,
        width: 180,
        type: `string`,
        valueGetter: (value, row) => row.parentContactName || ``,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (!row.selectable || !row.parentContactName) return null;

          if (row.parentContactId) {
            return (
              <WrappedLink to={ `/contacts/${row.parentContactId}` }>
                <Paragraph
                  variant={ `link` }
                  as={ `span` }
                >
                  { row.parentContactName }
                </Paragraph>
              </WrappedLink>
            );
          }

          return <span>{ row.parentContactName }</span>;
        },
      },
      {
        field: ContactTableFields.contactPersonName,
        headerName: `Person`,
        width: 110,
        type: `string`,
        valueGetter: (value, row) => displayPhoneNumber(row.firstName, row.lastName),
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        sortable: false,
        renderCell: ({ row }) => {
          const personName = displayPhoneNumber(row.firstName, row.lastName);

          if (!row.personTags?.length) {
            return personName;
          }

          return (
            <div className={ `flex flex-wrap items-center gap-1` }>
              <span>{ personName }</span>
              {
                row.personTags.map(tag => (
                  <Badge
                    key={ `${row.id}_${tag}` }
                    className={ `max-w-fit max-h-fit` }
                    color={ `yellow` }
                    size={ `xxs` }
                  >
                    { tag }
                  </Badge>
                ))
              }
            </div>
          );
        },
      },
      {
        field: ContactTableFields.email,
        headerName: `Email`,
        width: 200,
        type: `string`,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains` || op.value === `isEmpty`),
        sortable: false,
        cellClassName: params => {
          if (params.row.emailError) {
            return `text-orange-600`;
          }
        },
        renderCell: ({ row }) => {
          return (
            <div>
              <div className={ `flex flex-wrap` }>
                <Tooltip title={ row.emailError || row.email }>
                  <Paragraph
                    as={ `span` }
                    variant={ row.email ? undefined : `help` }
                  >
                    { row.email || `(no email)` }
                  </Paragraph>
                </Tooltip>
                <When condition={ row.email }>
                  <Tooltip title={ row.includeInNotifications ? `Included in notifications` : `Excluded in notifications` }>
                    <span>
                      <Badge
                        className={ `ml-2 max-w-fit` }
                        color={ row.includeInNotifications ? `green` : `gray` }
                      >
                        { row.includeInNotifications ? `✔` : `✘` }
                      </Badge>
                    </span>
                  </Tooltip>
                </When>
              </div>

              <Paragraph>
                { displayPhoneNumber(row.mobileCountryCode, row.mobileNumber) }
                <When condition={ row.mobileNumberInvalid }>
                  <InvalidMobileFixButton onClick={ () => openMobileFix(row.contactId) } />
                </When>
              </Paragraph>
            </div>
          );
        },
      },
      {
        field: ContactTableFields.externalAggregateNames,
        headerName: externalAggregateNameLabel,
        width: 100,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: externalAggregateNameOptions,
        valueFormatter: (value, row) => row.externalAggregateNames.join(`, `),
      },
      {
        field: ContactTableFields.frozen,
        headerName: `Frozen`,
        width: 80,
        type: `boolean`,
        filterOperators: getGridBooleanOperators(),
        renderCell: params => {
          if (params.row.selectable) {
            if (params.row.frozen) {
              return (
                <Tooltip title={ `Contact is frozen. No policies will apply.` }>
                  <span className={ `ml-6 w-full h-full flex items-center` }>
                    <LockClosedIcon className={ `h-5 w-5 text-blue-400` } />
                  </span>
                </Tooltip>
              );
            }

            return (
              <Tooltip title={ `Contact not frozen` }>
                <span className={ `ml-6 w-full h-full flex items-center` }>
                  <LockOpenIcon className={ `h-5 w-5 text-gray-200` } />
                </span>
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        field: `notes`,
        headerName: `Notes`,
        width: 70,
        sortable: false,
        filterable: false,
        disableExport: true,
        align: `center`,
        headerAlign: `center`,
        renderCell: ({ row }) => row.selectable && (
          <NotesCellButton
            resource={ { type: `contact`, id: row.contactId } }
            latestNote={ row.latestNote }
          />
        ),
      },
      {
        field: ContactTableFields.outstandingBalance,
        type: `number`,
        headerName: `Outstanding`,
        valueGetter: (value, row) => toDecimal(row.outstandingBalanceHome),
        valueFormatter: value => {
          return currencyFormatter(currentOrg?.baseCurrency)(value);
        },
        renderCell: params => {
          if (!params.row.selectable) return null;
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
      },
      {
        field: ContactTableFields.overdueBalance,
        type: `number`,
        headerName: `Overdue`,
        cellClassName: `text-red-500`,
        valueGetter: (value, row) => toDecimal(row.overdueBalanceHome),
        valueFormatter: value => {
          return currencyFormatter(currentOrg?.baseCurrency)(value);
        },
        renderCell: params => {
          if (!params.row.selectable) return null;
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
      },
      {
        field: ContactTableFields.totalRevenueLast12Months,
        type: `number`,
        headerName: `Revenue (Last 12 Months)`,
        valueGetter: (value, row) => toDecimal(row.totalRevenueHomeLast12Months),
        valueFormatter: value => {
          return currencyFormatter(currentOrg?.baseCurrency)(value);
        },
        renderCell: params => {
          if (!params.row.selectable) return null;
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 120,
      },
      {
        field: ContactTableFields.unpaidInvoiceCount,
        headerName: `No. of Unpaid Invoices`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.unpaidInvoiceCount,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.oldestDaysOverdue,
        headerName: `Oldest Overdue Invoice`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.oldestDaysOverdue,
        // When value is more than 0, set classname
        cellClassName: params => {
          if (params.value > 0) {
            return `text-red-500 font-bold`;
          }
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.avgDaysToPay,
        headerName: `Avg. Days to Pay`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.avgDaysToPay || 0,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.creditScore,
        headerName: `Credit Score`,
        description: `0-100 payment behaviour score. Contacts with no payment history and nothing outstanding have no score and never match a filter`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.creditScore ?? null,
        renderCell: ({ row }) => {
          if (!row.selectable || row.creditScore === null || row.creditScore === undefined) return null;

          return <span className={ classNames(`font-semibold`, creditScoreTone(row.creditScore).text) }>{ row.creditScore }</span>;
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.daysSinceLastCall,
        headerName: `Days Since Last Call`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.daysSinceLastCall,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.daysSinceLastText,
        headerName: `Days Since Last Text`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.daysSinceLastText,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.daysSinceLastPayment,
        headerName: `Days Since Last Payment`,
        type: `number`,
        align: `center`,
        valueGetter: (value, row) => row.daysSinceLastPayment,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        width: 80,
      },
      {
        field: ContactTableFields.latestEmailEvent,
        headerName: `Latest Email Event`,
        type: `singleSelect`,
        align: `center`,
        valueOptions: Object.values(SendgridMessageEventType).map(event => ({ value: event, label: emailStatusLabel(event) })),
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `isAnyOf`),
        renderCell: ({ row }) => <EmailStatusPill status={ row.latestEmailEvent } />,
        width: 120,
      },
      {
        field: ContactTableFields.assignee,
        type: `singleSelect`,
        headerName: `Assignee`,
        width: 140,
        valueGetter: (value, row) => row.assignedUserId,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: [{ value: INSIGHTS_UNASSIGNED, label: `Unassigned` }, ...currentOrgUsers],
        renderCell: ({ row }) => {
          if (!row.selectable) return null;

          if (!row.assignedUser) {
            return (
              <Paragraph
                as={ `span` }
                variant={ `help` }
              >
                { `Unassigned` }
              </Paragraph>
            );
          }

          const displayName = `${ row.assignedUser.firstName || `` } ${ row.assignedUser.lastName || `` }`.trim() || row.assignedUser.email;

          return (
            <Paragraph as={ `span` }>
              { displayName }
            </Paragraph>
          );
        },
      },

      // HIDDEN
      {
        field: ContactTableFields.paidniceGroup,
        headerName: `Group`,
        type: `singleSelect`,
        valueGetter: (value, row) => row.groupId,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is` || op.value === `not`),
        valueOptions: groupOptions,
      },
      {
        field: ContactTableFields.status,
        headerName: `Status`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: [
          { value: ContactStatus.ACTIVE, label: `Active` },
          { value: ContactStatus.ARCHIVED, label: `Archived` },
        ],
      },
      {
        field: ContactTableFields.hasActivePaymentPlan,
        headerName: `Payment plan`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: [
          { value: true, label: `Has active payment plan` },
          { value: false, label: `No active payment plan` },
        ],
      },
      {
        field: ContactTableFields.tags,
        headerName: `Contact tags`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: tagOptions,
      },
      {
        field: ContactTableFields.personTags,
        headerName: `Person tags`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: tagOptions,
      },
      {
        field: ContactTableFields.mobileNumber,
        headerName: `Mobile`,
        type: `string`,
        filterOperators: [
          ...getGridStringOperators().filter(op => op.value === `contains` || op.value === `isEmpty`),
          // Server side filter on contact_person.mobile_number_invalid
          {
            label: `is invalid`,
            value: `isInvalid`,
            getApplyFilterFn: () => null,
            requiresFilterValue: false,
          },
        ],
        valueGetter: (value, row) => displayPhoneNumber(row.mobileCountryCode, row.mobileNumber),
        renderCell: ({ row, value }) => (
          <span>
            { value }
            <When condition={ row.mobileNumberInvalid }>
              <InvalidMobileFixButton onClick={ () => openMobileFix(row.contactId) } />
            </When>
          </span>
        ),
      },
      {
        field: ContactTableFields.externalAccountNumber,
        headerName: externalAccountNumberLabel,
        type: `string`,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
      },
      {
        field: ContactTableFields.includeInNotifications,
        headerName: `Include In Notifications`,
        type: `boolean`,
        filterOperators: getGridBooleanOperators(),
        description: `Is this contact person included in email notifications?`,
        sortable: false,
        filterable: false,
      },
      {
        field: ContactTableFields.portalUrl,
        headerName: `Portal URL`,
        type: `string`,
        filterable: false,
        sortable: false,
        valueGetter: (value, row) => `${env.portalWebUrl}/c/${row.hash}`,
      },
      {
        field: ContactTableFields.externalId,
        headerName: `External ID`,
        type: `string`,
        filterable: false,
        sortable: false,
        valueGetter: (value, row) => row.externalId,
      },
    ];

    if (accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS)) {
      // Add the job/project columns
      const projectJobRows: GridColDef<TransformedRows>[] = [
        {
          field: ContactTableFields.isJob,
          headerName: `Job`,
          valueGetter: (value, row) => row.externalIsJob,
          width: 60,
          type: `boolean` as const,
          filterOperators: getGridBooleanOperators(),
        },
        {
          field: ContactTableFields.isProject,
          headerName: `Project`,
          width: 60,
          valueGetter: (value, row) => row.externalIsProject,
          type: `boolean` as const,
          filterOperators: getGridBooleanOperators(),
        },
      ];

      result.push(...projectJobRows);
    }

    return result;
  }, [externalAggregateNameLabel, externalAccountNumberLabel, externalAggregateNameOptions, tagOptions, groupOptions, currentOrg, currentOrgUsers]);

  const selectedContacts = useMemo(() => {
    const extractedIds: number[] = [];

    rowSelectionModel.ids.forEach(rowId => {
      if (typeof rowId === `string`) {
        const parts = rowId.split(`_`);
        if (parts.length > 0) {
          extractedIds.push(Number(parts[0]));
        }
      }
    });

    return [...new Set([...extractedIds])];
  }, [rowSelectionModel]);

  function onContactSync() {
    syncContacts(currentOrg?.id);
  }

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div
      style={ { height: tableHeight } }
      className={ `flex flex-col` }
    >
      <When condition={ context !== `group` }>
        <div className={ `mb-4 flex justify-between items-center` }>
          { /* Title */ }
          <div>
            <Heading
              variant={ `default` }
              size={ `xl` }
            >
              { `Contacts` }
            </Heading>
            <Paragraph
              variant={ `help` }
            >
              { `Contacts will be automatically imported as they are created or updated in ${useAccountingSystemName()} in real time.` }
            </Paragraph>
          </div>

          { /* Resync / Ops */ }
          <div className={ `mt-4 sm:mt-0 sm:ml-16 sm:flex-none` }>
            <Tooltip title={ `Force a re-sync of contacts now.` }>
              <span>
                <Button
                  onClick={ onContactSync }
                  loading={ syncContactsLoading }
                  disabled={ currentOrg?.contactSyncInProgress || syncContactsLoading }
                  color={ `paidniceGreenOutline` }
                  size={ `sm` }
                >
                  { currentOrg?.contactSyncInProgress ? `Checking for updates...` : `Check for updates` }
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </When>

      <When condition={ totalVersionOneCount > 0 }>
        <Notification
          type={ `warning` }
          className={ `mt-4 mb-0` }
        >
          {
            currentOrg?.contactSyncInProgress ?
              `${totalVersionOneCount} contacts are still waiting to be synced. They will be available shortly.` :
              `${totalVersionOneCount} contacts have not yet been synced. Please click sync or wait for the next daily sync.`
          }
        </Notification>
      </When>

      <DatagridCard className={ `flex-1` }>
        <DataGridPro
          apiRef={ apiRef }
          columns={ columns }
          rows={ flattenedContacts }
          loading={ isLoading || isFetching }
          showToolbar
          initialState={ {
            columns: {
              columnVisibilityModel: defaultColumnVisibilityModel,
            },
          } }
          getRowHeight={ () => `auto` }
          // Tree data
          treeData
          getTreeDataPath={ getTreeDataPath }
          groupingColDef={ groupingColDef }
          defaultGroupingExpansionDepth={ 1 }
          getRowClassName={ ({ row }) => row.selectable ? `` : `text-gray-500` }

          // Filter
          filterDebounceMs={ 300 }
          filterMode={ `server` }
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          // headerFilters
          // headerFilterHeight={ 54 }

          // Pagination
          pagination
          paginationMode={ `server` }
          paginationModel={ paginationModel }
          onPaginationModelChange={ setPaginationModel }
          pageSizeOptions={ [10, 25, 50, 100, 250] }
          rowCount={ totalCount }

          // Sort
          sortModel={ sortModel }
          onSortModelChange={ setSortModel }
          sortingMode={ `server` }

          // Selection
          checkboxSelection
          rowSelectionModel={ rowSelectionModel }
          onRowSelectionModelChange={ model => onRowSelectionChangeWrapper(model, flattenedContacts.map(i => i.id), setRowSelectionModel) }
          keepNonExistentRowsSelected
          isRowSelectable={ ({ row }) => row.selectable }

          // Misc
          disableVirtualization
          disableColumnMenu

          // Styling
          rowHeight={ 76 }
          columnHeaderHeight={ 42 }
          className={ `bg-white` }
          sx={ {
            ...commonConfig.headerStyle,
            ...commonConfig.toolbarStyle,
            border: context === `group` ? `none` : undefined,
            '--DataGrid-overlayHeight': `300px`,
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: `8px` },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: `10px` },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: `22px` },
          } }
          // Slots
          slots={ {
            toolbar: CustomToolbar,
            filterPanel: ApiSavedFilterPanel,
          } }
          slotProps={ {
            columnsManagement: {
              getTogglableColumns: cols => getTogglableColumns(cols, neverShowColumns),
            },
            toolbar: {
              showQuickFilter: true,
              rowSelectionModel,
              setRowSelectionModel,
              selectedContacts,
              onRemoveFilter,
            },
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
            filterPanel: {
              savedFilterType: SavedFilterType.Contact,
              exportParams: savedFilterExportParams,
              filterFormProps: {
                filterColumns: args => filterColumns(args, context === `group` ? [ContactTableFields.paidniceGroup] : [], true),
              },
              getColumnForNewFilter: getColumnForNewFilter,
              logicOperators: [GridLogicOperator.And],
            },
          } }
        />
      </DatagridCard>

      <MobileNumberFixModalContent
        open={ mobileFix.open }
        hide={ () => setMobileFix(current => ({ ...current, open: false })) }
        contactId={ mobileFix.contactId }
      />
    </div>
  );
}

function CustomToolbar({ rowSelectionModel, onRemoveFilter, setRowSelectionModel, selectedContacts }: ToolbarProps) {
  const currentOrg = useGetSelectedOrganisation();
  const { data: groups } = useGroups();
  const [addContactsToGroup, { isLoading }] = useAddContactsToGroupMutation();
  const [setParentOpen, setSetParentOpen] = useState(false);
  const tableParams = useContactTableParams();
  const [exportAllContacts] = useExportAllContactsMutation();

  async function onExportAll(filename: string | undefined, columns: string[]) {
    try {
      const response = await exportAllContacts({
        ...tableParams,
        organisationId: currentOrg?.id,
        filename,
        columns,
      }).unwrap();

      toast.success(`Export queued. The CSV will be emailed to ${response.email} shortly.`);
    }
    catch (e) {
      console.error(e);
      toast.error(`Could not queue the export. Please try again.`);
    }
  }

  const groupOptions = useMemo(() => {
    if (!groups) return [];

    return groups.map(g => ({
      label: g.title,
      value: g.id,
    }));
  }, [groups]);

  async function onMoveToGroup(e) {
    try {
      await addContactsToGroup({
        contactIds: selectedContacts,
        id: e.value,
        organisationId: currentOrg.id,
      });

      setRowSelectionModel({
        type: `include`,
        ids: new Set(),
      });
    }
    catch (e) {
      console.error(e);
    }
  }

  return (
    <CustomGenericToolbar
      rowSelectionModel={ rowSelectionModel }
      onRemoveFilter={ onRemoveFilter }
      onExportAll={ onExportAll }
      context={ `contacts` }
      leftActions={
        <SaveFilterButton type={ SavedFilterType.Contact }
          exportParams={ toSavedFilterExportParams(tableParams) } />
      }
    >
      <div className={ `flex flex-wrap gap-2` }>
        <Dropdown
          size={ `sm` }
          buttonColor={ `white` }
          position={ `right` }
          label={
            selectedContacts.length === 0 ?
              `Move contacts to group` :
              `Move ${selectedContacts.length} contacts to group`
          }
          options={ [groupOptions] }
          disabled={ !selectedContacts.length|| isLoading }
          onSelect={ onMoveToGroup }
        />

        <ApplyTags
          selectedContacts={ selectedContacts }
        />

        <BulkAssignContact
          selectedContacts={ selectedContacts }
        />

        <FreezeContacts
          selectedContacts={ selectedContacts }
        />

        <QuickActions
          selectedContacts={ selectedContacts }
          onSetParent={ () => setSetParentOpen(true) }
        />

        <CheckMobileNumbersButton />

        <FixSelectedMobileNumbersButton selectedContacts={ selectedContacts } />

        <SetParentContact
          selectedContacts={ selectedContacts }
          open={ setParentOpen }
          setOpen={ setSetParentOpen }
        />
      </div>
    </CustomGenericToolbar>
  );
}

function CustomGridTreeDataGroupingCell() {
  return null;
}
